import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { Popover, MenuItem, Typography } from '@mui/material';

import { useToast } from 'src/hooks/useToast';
import { useModal, useConfirmModal } from 'src/hooks/useModal';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { deleteTag } from 'src/services/tag.service';
import { getTicketsByTag } from 'src/services/ticket.service';

import Iconify from 'src/components/iconify';

import { iconStytle } from './style';

function MenuPopover({
  popoverAnchorEl,
  handleCloseMenu,
  selectedItem,
  handleFetchTags,
  AddUpdateTags,
  setSelectedTags,
  departmentList,
}) {
  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();
  const { showModal, hideModal } = useModal();
  const { showToast, hideToast } = useToast();

  const confirmTagDeletion = async () => {
    if (!selectedItem?.id) return;

    try {
      const tickets = await getTicketsByTag(selectedItem.id);
      if (tickets?.length > 0) {
        showLinkedTicketsWarningModal();
      } else {
        showDeleteConfirmModal();
      }
    } catch (error) {
      console.error('Error checking tickets by tag:', error);
      showDeleteConfirmModal();
    }
  };

  const RemoveTag = async (id, isConfirmedDelete = false) => {
    try {
      await deleteTag(id, isConfirmedDelete);
      showToast({
        message: t('successMessages.tagDeletedSuccessfully'),
        severity: 'success',
        props: { hideToast },
      });
      handleFetchTags();
      setSelectedTags([]);
    } catch (error) {
      if (error.response?.data === 'Tag is related to tickets.') {
        showLinkedTicketsWarningModal();
      } else {
        showToast({
          message: t('errorMessages.errorDeletingTag'),
          severity: 'error',
          props: { hideToast },
        });
      }
    }
  };

  const { showConfirmModal: showLinkedTicketsWarningModal } = useConfirmModal(
    t('pages.tagsPage.confirmDelete'),
    `${t('pages.tagsPage.tagRelatedWithTicketAreYouSure')} ${selectedItem?.name}`,
    async () => RemoveTag(selectedItem?.id, true)
  );

  const { showConfirmModal: showDeleteConfirmModal } = useConfirmModal(
    t('pages.tagsPage.confirmDelete'),
    `${t('pages.tagsPage.areYouSure')} ${selectedItem?.name}`,
    async () => RemoveTag(selectedItem?.id)
  );
  return (
    <Popover
      open={Boolean(popoverAnchorEl)}
      anchorEl={popoverAnchorEl}
      onClose={handleCloseMenu}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        sx: {
          p: 1,
          minwidth: 140,
          '& .MuiMenuItem-root': {
            px: 1,
            typography: 'body2',
            borderRadius: 0.75,
          },
        },
      }}
    >
      <MenuItem
        onClick={() => {
          showModal({
            title: t('pages.tagsPage.updateTag'),
            Component: AddUpdateTags,
            props: {
              hideModal,
              handleFetchTags,
              row: selectedItem,
              departmentList,
            },
          });
          handleCloseMenu();
        }}
      >
        <Iconify icon="eva:edit-fill" sx={iconStytle} />
        <Typography
          variant="body2"
          sx={{
            fontSize: isSmallScreen && '0.7rem',
          }}
        >
          {t('popover.edit')}
        </Typography>
      </MenuItem>

      <MenuItem
        sx={{ color: 'error.main' }}
        onClick={() => {
          confirmTagDeletion();
          handleCloseMenu();
        }}
      >
        <Iconify icon="eva:trash-2-outline" sx={iconStytle} />
        <Typography
          variant="body2"
          sx={{
            fontSize: isSmallScreen && '0.7rem',
          }}
        >
          {t('popover.delete')}
        </Typography>
      </MenuItem>
    </Popover>
  );
}

MenuPopover.propTypes = {
  popoverAnchorEl: PropTypes.object,
  handleCloseMenu: PropTypes.func,
  selectedItem: PropTypes.object,
  handleFetchTags: PropTypes.func,
  AddUpdateTags: PropTypes.func,
  setSelectedTags: PropTypes.func,
  departmentList: PropTypes.array,
};

export default MenuPopover;
